"use client";

import { useMemo, useState } from "react";
import { formatCurrency } from "../lib/format";

interface SensitivityEntry {
  parameter: string;
  label: string;
  spread: number;
  baseROI: number;
  lowROI: number;
  highROI: number;
}

interface SensitivityTornadoProps {
  sensitivity: SensitivityEntry[];
  highlightedParameter?: string | null;
  onParameterClick?: (parameter: string) => void;
  maxItems?: number;
}

export function SensitivityTornado({
  sensitivity,
  highlightedParameter,
  onParameterClick,
  maxItems = 8,
}: SensitivityTornadoProps) {
  const [hoveredParam, setHoveredParam] = useState<string | null>(null);

  // Filter out zero-spread items, then slice
  const items = useMemo(
    () => sensitivity.filter((s) => s.spread > 0).slice(0, maxItems),
    [sensitivity, maxItems],
  );

  const bars = useMemo(() => {
    if (items.length === 0) return [];
    return items.map((s) => {
      const deltaLow = s.lowROI - s.baseROI;
      const deltaHigh = s.highROI - s.baseROI;
      const downside = Math.min(deltaLow, deltaHigh);
      const upside = Math.max(deltaLow, deltaHigh);
      return { ...s, downside, upside };
    });
  }, [items]);

  const maxExtent = useMemo(() => {
    if (bars.length === 0) return 1;
    const maxDown = Math.max(...bars.map((b) => Math.abs(b.downside)));
    const maxUp = Math.max(...bars.map((b) => Math.abs(b.upside)));
    return Math.max(maxDown, maxUp, 1);
  }, [bars]);

  if (bars.length === 0) return null;

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="mb-5">
        <h2 className="text-base font-semibold text-foreground">
          Sensitivity Analysis
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          How a &plusmn;10% change in each assumption affects annual net ROI
        </p>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-5 mb-4 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-400" />
          Downside
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-400" />
          Upside
        </span>
        <span className="ml-auto text-dim-foreground">Spread</span>
      </div>

      {/* Chart rows */}
      <div className="space-y-0.5">
        {bars.map((bar, idx) => {
          const isHighlighted = highlightedParameter === bar.parameter;
          const isHovered = hoveredParam === bar.parameter;
          const downsidePct = (Math.abs(bar.downside) / maxExtent) * 100;
          const upsidePct = (Math.abs(bar.upside) / maxExtent) * 100;
          // Fade effect: top items full opacity, lower items slightly faded
          const opacity = 1 - idx * 0.04;

          return (
            <button
              type="button"
              key={bar.parameter}
              onClick={() => onParameterClick?.(bar.parameter)}
              onMouseEnter={() => setHoveredParam(bar.parameter)}
              onMouseLeave={() => setHoveredParam(null)}
              className={`group relative grid items-center w-full text-left rounded-lg px-2 transition-all duration-150 ${
                isHighlighted
                  ? "bg-blue-50 ring-1 ring-blue-200 py-2"
                  : isHovered
                    ? "bg-surface-hover py-2"
                    : "py-1.5"
              }`}
              style={{
                gridTemplateColumns: "8.5rem 1fr 1fr 3.5rem",
                opacity,
              }}
            >
              {/* Label */}
              <span
                className={`text-[11px] text-right truncate pr-2 transition-colors leading-tight ${
                  isHighlighted
                    ? "text-info-foreground font-semibold"
                    : "text-secondary-foreground group-hover:text-foreground"
                }`}
                title={bar.label}
              >
                {bar.label}
              </span>

              {/* Left half: downside bar growing right-to-left */}
              <div className="relative h-6 flex items-center justify-end">
                <div
                  className={`h-full rounded-l-md transition-all duration-300 ${
                    isHighlighted
                      ? "bg-rose-500"
                      : "bg-rose-300 group-hover:bg-rose-400"
                  }`}
                  style={{
                    width: `${downsidePct}%`,
                    minWidth: downsidePct > 0 ? "3px" : 0,
                  }}
                />
              </div>

              {/* Right half: upside bar growing left-to-right */}
              <div className="relative h-6 flex items-center justify-start border-l border-border">
                <div
                  className={`h-full rounded-r-md transition-all duration-300 ${
                    isHighlighted
                      ? "bg-emerald-500"
                      : "bg-emerald-300 group-hover:bg-emerald-400"
                  }`}
                  style={{
                    width: `${upsidePct}%`,
                    minWidth: upsidePct > 0 ? "3px" : 0,
                  }}
                />
              </div>

              {/* Spread */}
              <span
                className={`text-[11px] text-right tabular-nums pl-2 ${
                  isHighlighted
                    ? "text-info-foreground font-semibold"
                    : "text-dim-foreground group-hover:text-secondary-foreground"
                }`}
              >
                {formatCurrency(bar.spread)}
              </span>

              {/* Hover detail tooltip — appears below the row */}
              {isHovered && (
                <div className="absolute left-1/2 -translate-x-1/2 -bottom-7 z-20 flex items-center gap-3 bg-gray-800 text-white text-[10px] font-medium px-3 py-1 rounded-md shadow-lg whitespace-nowrap pointer-events-none">
                  <span className="text-rose-300">
                    {formatCurrency(bar.downside, { signed: true })}
                  </span>
                  <span className="text-gray-500">|</span>
                  <span className="text-gray-300">
                    Base {formatCurrency(bar.baseROI)}
                  </span>
                  <span className="text-gray-500">|</span>
                  <span className="text-emerald-300">
                    {formatCurrency(bar.upside, { signed: true })}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
        <span className="text-[10px] text-dim-foreground">
          Click a bar to highlight its assumption slider
        </span>
        <span className="text-[10px] text-dim-foreground">
          Base: {formatCurrency(bars[0]?.baseROI ?? 0)}/yr
        </span>
      </div>
    </div>
  );
}
